import { kebabCase } from "change-case";
import {
  Prompt,
  models,
  defaultTemperature,
  defaultLength,
} from "./promptsSlice";

export const getVariablesKeys = (basePrompt?: string) => {
  const regex = /\{\{(.*?)\}\}/g;
  const matches = [];
  let match;

  while ((match = regex.exec(basePrompt || "")) !== null) {
    const baseVariableKey = match[1].trim();
    if (baseVariableKey.length > 0) matches.push(kebabCase(match[1].trim()));
  }

  return matches;
};

export const mergeVariables = (variables: any, newVariablesKeys: any) => {
  const output: any = {};
  newVariablesKeys.forEach((variableKey: string) => {
    output[variableKey] = variables[variableKey] ? variables[variableKey] : "";
  });
  return output;
};

export const getCurrentMonth = (date: Date) => {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${month}/${year}`;
};

export const getPastMonths = (numberOfMonths: number) => {
  const date = new Date();
  const output = [getCurrentMonth(date)];

  for (let i = 1; i < numberOfMonths; i++) {
    date.setMonth(date.getMonth() - 1);
    output.unshift(getCurrentMonth(date));
  }

  return output;
};

export const apiBaseURL =
  !process.env.NODE_ENV || process.env.NODE_ENV === "development"
    ? "http://localhost:8080"
    : "https://api.myuserprompt.com";

export function getPromptTitle(prompt: Prompt) {
  return prompt.title;
}

export function getPromptDate(prompt: Prompt) {
  if (!prompt.createdAt) return "";
  return new Date(prompt.createdAt).toDateString();
}

export function getModelModelId(modelId: string | undefined) {
  if (!modelId) return null;
  return models.find((currentModel) => currentModel.id === modelId);
}

function compileBasePrompt(basePrompt: string, variables: any) {
  return basePrompt.replace(/{{(.*?)}}/g, (_: any, word: string) => {
    const variableKey = kebabCase(word);
    return variables && variables[variableKey]
      ? variables[variableKey]
      : `{{${word}}}`;
  });
}

function compilePromptForOpenAI(prompt: Prompt) {
  const {
    audience,
    style,
    basePrompt,
    tone,
    persona,
    toExclude,
    toInclude,
    temperature,
    length,
    variables,
  } = prompt;

  let output =
    basePrompt && basePrompt !== ""
      ? `${compileBasePrompt(basePrompt, variables)}`
      : "";
  let addedPreviousInstructions = false;
  const guidelinesTitle = "\n\nGuidelines: \n";
  if (output.trim() !== "") {
    if (persona && persona?.trim() !== "") {
      addedPreviousInstructions = true;
      output += `${guidelinesTitle}• Pretend you are a ${persona?.toLowerCase()}`;
    }
    if (style && style?.trim() !== "") {
      output += addedPreviousInstructions ? "\n" : guidelinesTitle;
      addedPreviousInstructions = true;
      output += `• Write it in a ${style?.toLowerCase()} style`;
    }
    if (audience && audience?.trim() !== "") {
      output += addedPreviousInstructions ? "\n" : guidelinesTitle;
      addedPreviousInstructions = true;
      output += `• The audience is ${audience?.toLowerCase()}`;
    }
    if (tone?.length && tone?.length > 0) {
      output += addedPreviousInstructions ? "\n" : guidelinesTitle;
      addedPreviousInstructions = true;
      output += "• The tone should be ";
      tone.forEach((currentTone, idx) => {
        if (idx === 0) output += currentTone?.toLowerCase();
        else if (idx === tone.length - 1)
          output += ` and ${currentTone?.toLowerCase()}`;
        else output += `, ${currentTone?.toLowerCase()}`;
      });
    }
    if (toInclude?.trim() !== "") {
      output += addedPreviousInstructions ? "\n" : guidelinesTitle;
      addedPreviousInstructions = true;
      output += `• Include: ${toInclude}`;
    }
    if (toExclude?.trim() !== "") {
      output += addedPreviousInstructions ? "\n" : guidelinesTitle;
      addedPreviousInstructions = true;
      output += `• Exclude: ${toExclude}`;
    }
    if (length && Math.abs(defaultLength - length) > 10) {
      output += addedPreviousInstructions ? "\n" : guidelinesTitle;
      addedPreviousInstructions = true;
      output += `• The text should be about ${length} words long`;
    }
    if (
      Math.abs(defaultTemperature - (temperature || 0)) > 10
    ) {
      output += addedPreviousInstructions ? "\n" : guidelinesTitle;
      addedPreviousInstructions = true;
      output += `• Creativity Level: ${getCreativity((temperature || 0))}`;
    }
  }

  return output;
}

const getCreativity = (temperature: number) => {
  if (Math.abs(defaultTemperature - (temperature || 0)) <= 10) return "Default";
  if ((temperature || 0) > 150) return "Very High Creativity";
  if ((temperature || 0) > defaultTemperature) return "High Creativity";
  if ((temperature || 0) < 30) return "Very Low Creativity";
  if ((temperature || 0) < defaultTemperature) return "Low Creativity";
};

function compilePromptForAnthropic(prompt: Prompt) {
  const {
    audience,
    style,
    basePrompt,
    tone,
    persona,
    toExclude,
    toInclude,
    temperature,
    length,
    variables,
  } = prompt;

  let output =
    basePrompt && basePrompt !== ""
      ? `${compileBasePrompt(basePrompt, variables)}`
      : "";
  if (basePrompt?.trim() !== "") {
    if (audience) output += `\n <audience>${audience.toLowerCase()}</audience>`;
    if (style) output += `\n <style>${style.toLowerCase()}</style>`;
    if (persona) output += `\n <persona>${persona.toLowerCase()}</persona>`;
    if (tone && tone.length > 0)
      output += `\n <tone>${tone.join(",").toLowerCase()}</tone>`;
    if (toExclude) output += `\n <exclude>${toExclude.toLowerCase()}</exclude>`;
    if (toInclude) output += `\n <include>${toInclude.toLowerCase()}</include>`;
    if (Math.abs(defaultTemperature - (temperature || 0)) > 10)
      output += `\n <creativity>${getCreativity(
        temperature || 0
      )}</creativity>`;
    if (length && Math.abs(defaultLength - length) > 10)
      output += `\n <length>${length} words</length>`;
  }

  return output;
}

export function compilePrompt(prompt: Prompt) {
  const { model } = prompt;
  const selectedModel = models.find(
    (currentModel) => currentModel.id === model
  );

  if (selectedModel) {
    if (selectedModel.organization === "Anthropic")
      return compilePromptForAnthropic(prompt);

    return compilePromptForOpenAI(prompt);
  }

  return "";
}
